<template>
  <div v-if="parsedLabels.length > 0" class="label-chips-wrapper d-inline-flex flex-wrap align-items-center">
    <span
      v-for="(item, index) in parsedLabels"
      :key="item.key || index"
      class="label-chip"
      :class="[
        `label-chip--${size}`,
        { 'label-chip--clickable': clickable }
      ]"
      @click="clickable ? $emit('click', item.raw) : null"
    >
      <template v-if="item.hasPrefix">
        <span class="label-chip__prefix">{{ item.prefix }}:</span>
        <span class="label-chip__suffix">{{ item.suffix }}</span>
      </template>
      <template v-else>
        <span class="label-chip__plain">{{ item.text }}</span>
      </template>
      <feather-icon
        v-if="removable"
        icon="XIcon"
        size="12"
        class="label-chip__remove ml-25 cursor-pointer"
        @click.stop="$emit('remove', item.raw)"
      />
    </span>
  </div>
</template>

<script>
export default {
  name: 'LabelChip',
  props: {
    label: {
      type: [String, Object],
      default: null,
    },
    labels: {
      type: Array,
      default: null,
    },
    size: {
      type: String,
      default: 'sm',
      validator: val => ['sm', 'md'].includes(val),
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    parsedLabels() {
      const list = []
      if (this.labels && Array.isArray(this.labels)) {
        list.push(...this.labels)
      } else if (this.label) {
        list.push(this.label)
      }

      return list
        .filter(item => item !== null && item !== undefined && item !== '')
        .map((item, idx) => {
          let text = ''
          let key = idx

          if (typeof item === 'string') {
            text = item
            key = `${item}-${idx}`
          } else if (typeof item === 'object') {
            text = item.label || item.name || ''
            key = item.id || `${text}-${idx}`
          }

          const colonIndex = text.indexOf(':')
          if (colonIndex > 0) {
            return {
              raw: item,
              key,
              text,
              hasPrefix: true,
              prefix: text.substring(0, colonIndex).trim(),
              suffix: text.substring(colonIndex + 1).trim(),
            }
          }

          return {
            raw: item,
            key,
            text,
            hasPrefix: false,
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

// Customizable SCSS variables for easy adjustments
$label-chip-font-size-sm: 0.82rem !default;
$label-chip-font-size-md: 0.9rem !default;
$label-chip-padding-y-sm: 0.12rem !default;
$label-chip-padding-x-sm: 0.45rem !default;
$label-chip-padding-y-md: 0.2rem !default;
$label-chip-padding-x-md: 0.55rem !default;

.label-chips-wrapper {
  gap: $spacer * 0.25;
  vertical-align: middle;
}

.label-chip {
  display: inline-flex;
  align-items: center;
  background-color: $gray-50;
  border: 1px solid darken($border-color, 8%);
  border-radius: $border-radius-sm;
  padding: $label-chip-padding-y-sm $label-chip-padding-x-sm;
  line-height: 1.35;
  font-size: $label-chip-font-size-sm;
  white-space: nowrap;
  user-select: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &--md {
    padding: $label-chip-padding-y-md $label-chip-padding-x-md;
    font-size: $label-chip-font-size-md;
    border-radius: $border-radius;
  }

  &--clickable {
    cursor: pointer;
    &:hover {
      background-color: darken($gray-50, 4%);
      border-color: darken($border-color, 14%);
    }
  }

  &__prefix {
    color: $body-color;
    font-weight: $font-weight-normal;
  }

  &__suffix {
    color: $headings-color;
    font-weight: $font-weight-bolder;
  }

  &__plain {
    color: $headings-color;
    font-weight: $font-weight-bold;
  }

  &__remove {
    color: $secondary;
    transition: color 0.15s ease;
    &:hover {
      color: $danger;
    }
  }
}

// Dark layout styling
.dark-layout {
  .label-chip {
    background-color: $theme-dark-card-bg;
    border-color: lighten($theme-dark-border-color, 8%);

    &--clickable:hover {
      background-color: $theme-dark-table-header-bg;
      border-color: lighten($theme-dark-border-color, 16%);
    }

    &__prefix {
      color: $theme-dark-body-color;
    }

    &__suffix {
      color: $theme-dark-headings-color;
    }

    &__plain {
      color: $theme-dark-headings-color;
    }

    &__remove {
      color: $theme-dark-text-muted-color;
      &:hover {
        color: $danger;
      }
    }
  }
}
</style>
